import { FormControl, Grid, MenuItem, Paper, Select, Typography } from '@mui/material';
import { subMonths, startOfMonth, endOfMonth, startOfWeek, subWeeks, endOfWeek, format } from 'date-fns';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useEffect, useState } from 'react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface LastMonthsDateRange {
  monthNumber: number;
}

const lastMonthsDateRange = ({ monthNumber }: LastMonthsDateRange) => {
  const startDate = subMonths(startOfMonth(new Date()), monthNumber);
  const endDate = endOfMonth(subMonths(startOfMonth(new Date()), 1));
  return {
    startDate: format(startDate, 'MM/dd/yyyy'),
    endDate: format(endDate, 'MM/dd/yyyy'),
  };
};

const lastWeeksDateRange = () => {
  const startDate = startOfWeek(subWeeks(new Date(), 1));
  const endDate = endOfWeek(subWeeks(new Date(), 1));
  return {
    startDate: format(startDate, 'MM/dd/yyyy'),
    endDate: format(endDate, 'MM/dd/yyyy'),
  };
};

interface BarChartComponent {
  staticData: any;
  onDateRange: (startDate: string, endDate: string) => void;
}
export const BarChartComponent = ({ staticData, onDateRange }: BarChartComponent) => {
  const [filterValue, setFilterValue] = useState<string>('3');
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true, // This line adds square symbols to the legend
        },
      },
    },
    scales: {
      // to remove the labels
      x: {
        ticks: {
          display: false,
        },
      },
      // to remove the y-axis labels
      y: {
        ticks: {
          display: false,
          beginAtZero: true,
        },
      },
    },
  };

  const labels = ['Workorder'];

  const data = {
    labels,
    datasets: [
      {
        label: 'Forecast',
        data: [staticData?.countForecasted],
        backgroundColor: '#000205',
        pointStyle: 'rect',
      },
      {
        label: 'Scheduled',
        data: [staticData?.countScheduled],
        backgroundColor: '#E120F1',
        pointStyle: 'rect',
      },
      {
        label: 'Property Not Ready',
        data: [staticData?.countNotready],
        backgroundColor: '#0426D1',
        pointStyle: 'rect',
      },
      {
        label: 'Pass',
        data: [staticData?.countPass],
        backgroundColor: '#CB4335',
        pointStyle: 'rect',
      },
      {
        label: 'Fail',
        data: [staticData?.countFail],
        backgroundColor: '#BFC9CA',
        pointStyle: 'rect',
      },
      {
        label: 'Complete',
        data: [staticData?.countCompleted],
        backgroundColor: '#28B463',
        pointStyle: 'rect',
      },
      {
        label: 'Canceled',
        data: [staticData?.countCancelled],
        backgroundColor: '#F4D03F',
        pointStyle: 'rect',
      },
      {
        label: 'Waived',
        data: [staticData?.countWaived],
        backgroundColor: '#E67E22',
        pointStyle: 'rect',
      }
    ],
  };

  useEffect(() => {
    const dateRange = lastMonthsDateRange({ monthNumber: 3 });
    onDateRange(dateRange.startDate, dateRange.endDate);
  }, []);

  const handleChange = (event: any) => {
    setFilterValue(event.target.value);
    let dateRange: any;
    if (event.target.value == 'Previous Week') {
      dateRange = lastWeeksDateRange();
    } else dateRange = lastMonthsDateRange({ monthNumber: Number(event.target.value) });
    onDateRange(dateRange.startDate, dateRange.endDate);
  };

  return (
    <Paper elevation={0} style={{ padding: 20 }}>
      <Grid container spacing={1}>
        <Grid item lg={5} sm={5} xl={5} xs={5}>
          <Typography variant="body1" color="textPrimary">
            {`WORK ORDERS ${staticData?.countWorkorders ?? 0}`}
          </Typography>
        </Grid>
        <Grid item lg={7} sm={7} xl={7} xs={7}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="flex-start">
            <Typography variant="body1" color="text.secondary" style={{ paddingRight: 10 }}>
              Select Range
            </Typography>
            <FormControl style={{ width: 160 }}>
              <Select
                size="small"
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={filterValue}
                onChange={handleChange}
              >
                {/* <MenuItem value=""><em>NONE</em></MenuItem> */}
                <MenuItem value="Previous Week">Previous Week</MenuItem>
                <MenuItem value="3">Last 3 Months</MenuItem>
                <MenuItem value="6">Last 6 Months</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Bar options={options} data={data} />
        </Grid>
      </Grid>
    </Paper>
  );
};
